import React, { useState, useMemo } from 'react';
import { showToast } from '@/components/ui/Toast';
import { useTranslation } from 'react-i18next';
import { DndContext, DragEndEvent, closestCorners, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';

import { Shield, Users, Layers, Lock, PlusCircle, Sparkles, X, Loader2, Plus, Trophy, CheckCircle, XCircle, Mic, MicOff, Library } from 'lucide-react';
import { TabBar, TabItem } from '@/components/ui/TabBar';
import { EmptyState } from '@/components/ui/EmptyState';
import { UpgradeModal } from '@/components/ui/UpgradeModal';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import type { PlanLimitResult } from '@/hooks/usePlanLimits';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app'; // 🛡️ Added for Scope Visuals and Task Creation
import { getFirestore, doc, updateDoc } from 'firebase/firestore';

const TEMPLATE_SETS = [
  { id: 'weekly_household',  label: 'Weekly Household',      description: 'Core recurring chores for any household.' },
  { id: 'seasonal_spring',   label: 'Spring Cleaning',        description: 'Deep-clean checklist for the spring season.' },
  { id: 'seasonal_fall',     label: 'Fall Prep',              description: 'Winterize and prep tasks for colder months.' },
  { id: 'monthly_admin',     label: 'Monthly Admin',          description: 'Bills, subscriptions, and paperwork reminders.' },
  { id: 'garbage_recycling', label: 'Garbage & Recycling',    description: 'Weekly bin and recycling rotation schedule.' },
  { id: 'car_maintenance',   label: 'Car Maintenance',        description: 'Oil changes, tire rotations, and service reminders.' },
  { id: 'grocery_staples',   label: 'Grocery Staples',        description: 'Standing list of pantry essentials to restock.' },
] as const;

// 🎯 Types and Hooks
import { AppState, AppAction, KanbanTask, BoardConfig, TaskStatus, TemplateType, DataScope, Role } from '@/types';
import { useTaskState } from '../features/kanban-engine/hooks/useTaskState';
import { useAiAction } from '@/hooks/useAiAction';
import { KanbanBoard } from '../features/kanban-engine/components/KanbanBoard';
import { TaskModal } from '../features/kanban-engine/components/TaskModal';

const DEFAULT_TASKS_BOARD: BoardConfig = {
  id: 'main-tasks-board',
  title: 'My Action Protocols',
  templateType: TemplateType.ACTION,
  allowedStatuses: ['todo', 'in_progress', 'pending_approval', 'finalized'],
  dataScope: 'BOTH',
  icon: 'clipboard-list',
  color: '#4f46e5',
  visibility: 'member',
  isCollapsed: false,
  defaultGroupBy: 'priority',
  activeFilters: []
};

interface TasksProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

/**
 * 🏛️ Tasks View (Hybrid Scoping Edition)
 * Orchestrates task visibility across Personal and Shared boundaries.
 */
export const Tasks: React.FC<TasksProps> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const currentUserId = state.currentUser?.id || 'guest';
  const currentHouseholdId = state.currentHouseholdId;
  const userTier = state.currentUser?.subscription?.tier;
  const isPaidUser = userTier && userTier !== 'FREE';

  // Tab state
  const [viewTab, setViewTab] = useState<'tasks' | 'templates'>('tasks');
  const [applyingTemplate, setApplyingTemplate] = useState<string | null>(null);
  const [templateMessage, setTemplateMessage] = useState<string | null>(null);

  // ➕ Task Modal State
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<KanbanTask | null>(null);
  const [initialTaskData, setInitialTaskData] = useState<{ title?: string; description?: string; dueDate?: string } | undefined>(undefined);

  // 🚦 Plan limit gate
  const { checkLimit, isChecking: isCheckingLimit } = usePlanLimits(currentHouseholdId ?? null);
  const [upgradeLimitResult, setUpgradeLimitResult] = useState<(PlanLimitResult & { allowed: false }) | null>(null);

  const handleNewTaskClick = async () => {
    const result = await checkLimit('tasks');
    if (!result.allowed) {
      setUpgradeLimitResult(result as PlanLimitResult & { allowed: false });
      return;
    }
    setInitialTaskData(undefined);
    setSelectedTask(null);
    setShowTaskModal(true);
  };

  // 🎙️ Voice-to-Task State
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const { callAction: callVoiceAi } = useAiAction(currentHouseholdId);

  const handleVoiceCapture = async () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showToast('Voice capture is not supported in this browser. Try Chrome.', 'error');
      return;
    }
    if (isRecording) return;

    setIsRecording(true);
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = async (event: any) => {
      const transcript: string = event.results[0][0].transcript;
      setIsRecording(false);
      setIsProcessingVoice(true);
      try {
        const result = await callVoiceAi<{ title: string; description?: string; dueDate?: string; priority?: string }>('parseVoiceTask', { transcript });
        if (result?.title) {
          setInitialTaskData({ title: result.title, description: result.description, dueDate: result.dueDate });
          setSelectedTask(null);
          setShowTaskModal(true);
        }
      } finally {
        setIsProcessingVoice(false);
      }
    };
    recognition.onerror = () => setIsRecording(false);
    recognition.onend = () => setIsRecording(false);
    recognition.start();
  };

  // 🤖 AI Chore Planner State
  const [showChorePanel, setShowChorePanel] = useState(false);
  const [chorePlanDays, setChorePlanDays] = useState(7);
  const [generatedChores, setGeneratedChores] = useState<Array<{ title: string; assigneeName: string; dueDate: string; contributionPoints: number }>>([]);
  const [choreGenLoading, setChoreGenLoading] = useState(false);
  const [choreGenError, setChoreGenError] = useState<string | null>(null);
  const [addedChoreIds, setAddedChoreIds] = useState<Set<number>>(new Set());

  const { callAction } = useAiAction(currentHouseholdId);

  // 🏆 XP Toast state
  const [xpToast, setXpToast] = useState<{ xp: number; visible: boolean }>({ xp: 0, visible: false });

  const awardXpForTask = (task: KanbanTask) => {
    if (!currentHouseholdId) return;
    const priority = (task as any).priorityTier || (task as any).priority || 'P3';
    const fns = getFunctions(getApp());
    const award = httpsCallable<{ householdId: string; priority: string }, { xpEarned: number }>(fns, 'awardMemberPoints');
    award({ householdId: currentHouseholdId, priority })
      .then((res) => {
        const xp = res.data?.xpEarned ?? 10;
        setXpToast({ xp, visible: true });
        setTimeout(() => setXpToast(prev => ({ ...prev, visible: false })), 3000);
      })
      .catch(() => {}); // Non-fatal
  };

  // 🏠 Household members + admin check
  const currentHousehold = state.households.find(h => h.id === currentHouseholdId);
  const currentMember = currentHousehold?.members.find(m => m.userId === currentUserId);
  const isAdmin = currentMember?.role === Role.ADMIN || currentMember?.role === Role.OWNER;
  const householdMembers = currentHousehold?.members ?? [];

  // ✅ Pending approval resolution
  const [approvingTaskId, setApprovingTaskId] = useState<string | null>(null);

  const handleApproveTask = async (task: KanbanTask) => {
    if (!currentHouseholdId) return;
    setApprovingTaskId(task.id);
    try {
      const db = getFirestore(getApp());
      await updateDoc(doc(db, `households/${currentHouseholdId}/tasks/${task.id}`), {
        status: 'done',
        resolvedPendingApproval: true,
      });
      dispatch({ type: 'UPDATE_TASK', payload: { id: task.id, updates: { status: 'done' as TaskStatus, resolvedPendingApproval: true } } });
    } finally {
      setApprovingTaskId(null);
    }
  };

  const handleRejectTask = async (task: KanbanTask) => {
    if (!currentHouseholdId) return;
    setApprovingTaskId(task.id);
    try {
      const db = getFirestore(getApp());
      await updateDoc(doc(db, `households/${currentHouseholdId}/tasks/${task.id}`), {
        status: 'todo',
      });
      dispatch({ type: 'UPDATE_TASK', payload: { id: task.id, updates: { status: 'todo' as TaskStatus } } });
    } finally {
      setApprovingTaskId(null);
    }
  };

  // 🛡️ HYBRID SCOPE: Driven by global header toggle
  const activeScope: DataScope = state.activeDataScope ?? (currentHouseholdId ? 'BOTH' : 'PERSONAL');

  // 🛰️ Dnd-kit Sensors with activation constraint
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // 🎯 Filter global tasks to match the selected Scope and TemplateType
  const filteredActionTasks = useMemo(() => {
    return state.tasks.filter(t => {
      const isAction = t.templateType === TemplateType.ACTION;

      // 🛡️ SCOPE FIREWALL: Apply logic based on privacy boundary
      const isPersonal = t.scope === 'personal';
      const isShared = t.scope === 'household';

      let scopeMatches = true;
      if (activeScope === 'PERSONAL') scopeMatches = isPersonal;
      if (activeScope === 'SHARED') scopeMatches = isShared;
      // 'BOTH' implies scopeMatches is always true

      // 🔒 PRIVACY GUARD: Never show personal tasks owned by other users
      const ownershipMatch = isPersonal ? t.ownerId === currentUserId : true;

      return isAction && scopeMatches && ownershipMatch;
    });
  }, [state.tasks, activeScope, currentUserId]);

  // 🎯 Hook integration with scope-awareness

  const { tasks, moveTask, createTask, updateTask, deleteTask } = useTaskState(
    filteredActionTasks,
    currentUserId,
    dispatch,
    currentHouseholdId,
    activeScope, // 🛡️ DataScope argument
    state.households.find(h => h.id === currentHouseholdId)?.permissions?.[currentUserId] // 🛡️ Permissions argument
  );

  const handleApplyTemplate = async (templateSetId: string) => {
    setApplyingTemplate(templateSetId);
    setTemplateMessage(null);
    try {
      const fns = getFunctions(getApp());
      const applyFn = httpsCallable<{ templateSetId: string }, { success: boolean; count: number }>(fns, 'applyTaskTemplate');
      const result = await applyFn({ templateSetId });
      setTemplateMessage(`✓ ${t('tasks.template_applied', '{{count}} tasks added from template.', { count: result.data.count })}`);
    } catch (err: unknown) {
      setTemplateMessage(`Error: ${err instanceof Error ? err.message : 'Failed to apply template.'}`);
    } finally {
      setApplyingTemplate(null);
    }
  };

  const handleGenerateChoreSchedule = async () => {
    setChoreGenLoading(true);
    setChoreGenError(null);
    setGeneratedChores([]);
    setAddedChoreIds(new Set());
    const currentHousehold = state.households?.find(h => h.id === currentHouseholdId);
    const memberNames: string[] = (currentHousehold as any)?.members?.map((m: any) => m.displayName || m.name || 'Member')
      ?? (currentHousehold as any)?.memberIds?.map((_: string, i: number) => `Member ${i + 1}`)
      ?? ['Member'];
    const result = await callAction<{ tasks: Array<{ title: string; assigneeName: string; dueDate: string; contributionPoints: number }> }>(
      'generateChoreSchedule',
      { memberNames, daysAhead: chorePlanDays }
    );
    if (result?.tasks?.length) {
      setGeneratedChores(result.tasks);
    } else {
      setChoreGenError(t('tasks.chore_gen_error', 'Could not generate a chore plan. Please try again.'));
    }
    setChoreGenLoading(false);
  };

  const handleAddGeneratedChore = (chore: typeof generatedChores[number], index: number) => {
    const scope = activeScope === 'PERSONAL' ? 'personal' : 'household';
    createTask(
      chore.title,
      TemplateType.ACTION,
      DEFAULT_TASKS_BOARD.id,
      [],
      scope as 'personal' | 'household',
      { isEnabled: false, interval: 0, frequency: null, completionTrigger: false, lastResetDate: undefined },
      chore.dueDate
    );
    setAddedChoreIds(prev => new Set(prev).add(index));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeTaskId = String(active.id);
    // Extract targetStatus and destinationBoard from over.data provided by useDroppable
    const targetStatus = (over.data?.current?.status || '') as TaskStatus;
    const destinationBoard = over.data?.current?.board as BoardConfig | undefined;

    const task = tasks.find(t => t.id === activeTaskId);
    if (task && targetStatus && targetStatus !== task.status) {
      // 🛡️ PERMISSION CHECK: Verify user can move household tasks
      if (task.scope === 'household' && !state.households.find(h => h.id === currentHouseholdId)?.permissions?.[currentUserId]?.canManageTasks) {
        console.warn("Permission denied: Cannot move household task.");
        return;
      }
      // Pass the destinationBoard to moveTask
      moveTask(activeTaskId, targetStatus, destinationBoard);
      // Award XP when task moves to finalized
      if (targetStatus === 'finalized') awardXpForTask(task);
    }
  };

 const handleKanbanBoardMove = React.useCallback(
    (taskId: string, newStatus: TaskStatus, destinationBoard?: BoardConfig) => {
      // The KanbanBoard's onMoveTask now provides an optional destinationBoard.
      // Our useTaskState's moveTask already expects this third argument.

      const task = tasks.find(t => t.id === taskId);

      // 🛡️ PERMISSION CHECK: Verify user can move household tasks
      if (task?.scope === 'household' && !state.households.find(h => h.id === currentHouseholdId)?.permissions?.[currentUserId]?.canManageTasks) {
        console.warn("Permission denied: Cannot move household task.");
        return;
      }

      // Pass the destinationBoard directly to moveTask
      moveTask(taskId, newStatus, destinationBoard);
      // Award XP when task moves to finalized
      if (newStatus === 'finalized' && task) awardXpForTask(task);
    },
    // Ensure all dependencies are correctly listed for useCallback
    [moveTask, tasks, currentHouseholdId, currentUserId, state.households]
  );

  return (
    <>
    {xpToast.visible && (
      <div className="fixed bottom-6 right-6 z-[200] flex items-center gap-2 px-5 py-3 bg-brand text-white rounded-full shadow-lg font-black text-sm animate-in slide-in-from-bottom-5">
        <Trophy className="w-4 h-4 text-yellow-400" />
        {t('tasks.xp_earned', '+{{xp}} XP earned!', { xp: xpToast.xp })}
      </div>
    )}
    <div className="max-w-page-full mx-auto p-6 pb-32 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-6">
        <div className="mb-1">
          <div className="flex items-center gap-2">
            <Lock className="w-3 h-3 text-brand" />
            <p className="text-2xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300">
              {currentHouseholdId ? t('tasks.scope_shared', 'Hybrid Tenancy Active') : t('tasks.scope_personal', 'Personal Partition Mode')}
            </p>
          </div>
        </div>

        {/* Action Buttons Row */}
        <div className="flex flex-wrap items-center justify-end gap-3">
          {/* 🤖 AI CHORE PLANNER */}
          {currentHouseholdId && (
            isPaidUser ? (
              <button
                onClick={() => setShowChorePanel(true)}
                className="flex items-center gap-2 px-6 py-[11px] bg-transparent text-brand border border-brand rounded-full font-black uppercase tracking-widest text-xs hover:bg-brand/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand dark:focus-visible:ring-offset-slate-900 active:bg-brand/20 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">{t('tasks.ai_chore_planner', 'AI Chore Plan')}</span>
              </button>
            ) : (
              <button
                onClick={() => showToast(t('tasks.upgrade_for_ai', 'Upgrade to Basic or Premium to use AI Chore Planner.'), 'info')}
                className="flex items-center gap-2 px-6 py-[11px] bg-transparent text-slate-400 border border-slate-300 dark:border-slate-700 rounded-full font-black uppercase tracking-widest text-xs cursor-default"
                title={t('tasks.upgrade_for_ai', 'Upgrade to Basic or Premium to use AI Chore Planner.')}
              >
                <Lock className="w-4 h-4" />
                <span className="hidden sm:inline">{t('tasks.ai_chore_planner', 'AI Chore Plan')}</span>
              </button>
            )
          )}

          {/* 🎙️ VOICE-TO-TASK BUTTON */}
          <button
            onClick={handleVoiceCapture}
            disabled={isRecording || isProcessingVoice}
            className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full w-12 h-12 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            title="Voice to task"
          >
            {isProcessingVoice ? <Loader2 className="w-4 h-4 animate-spin" /> : isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>

          {/* ➕ CREATE TASK BUTTON */}
          <button
            onClick={handleNewTaskClick}
            disabled={isCheckingLimit}
            className="flex items-center gap-2 px-6 py-3 bg-brand text-white rounded-full font-black uppercase tracking-widest text-xs shadow-lg hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand dark:focus-visible:ring-offset-slate-900 active:bg-blue-800 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed disabled:shadow-none transition-all"
          >
            {isCheckingLimit ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-5 h-5" />}
            <span>{t('tasks.createTask', 'Create Task')}</span>
          </button>
        </div>
      </div>

      {/* 📑 TAB SELECTOR */}
      <TabBar
        tabs={([
          { id: 'tasks',     label: t('nav.tasks', 'Tasks') },
          { id: 'templates', label: t('tasks.tab_templates', 'Templates'), icon: <Library className="w-3.5 h-3.5" /> },
        ] as TabItem[])}
        activeTab={viewTab}
        onChange={id => setViewTab(id as 'tasks' | 'templates')}
        className="my-6 w-fit"
      />

      {/* 📚 TEMPLATES TAB */}
      {viewTab === 'templates' && (
        <div role="tabpanel" id="templates-panel" className="mb-6 animate-in fade-in duration-300">
          {!isPaidUser ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <Lock className="w-8 h-8 text-slate-400" />
              <p className="text-sm font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                {t('tasks.template_desc', 'Task templates require a Basic or Premium subscription.')}
              </p>
              <button
                onClick={() => window.open('/upgrade', '_blank')}
                className="px-6 py-2.5 bg-brand text-white text-xs font-black uppercase tracking-widest rounded-full hover:bg-brand-hover transition-colors"
              >
                {t('common.upgrade', 'Upgrade')}
              </button>
            </div>
          ) : (
            <>
              <p className="text-2xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-4">
                {t('tasks.template_desc', 'Apply a template set to instantly create recurring household tasks.')}
              </p>
              {templateMessage && (
                <div className={`mb-6 px-4 py-3 rounded-2xl text-sm font-bold ${templateMessage.startsWith('✓') ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
                  {templateMessage}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {TEMPLATE_SETS.map(tpl => (
                  <div key={tpl.id} className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-100 dark:border-slate-800 flex flex-col md:flex-row md:items-center md:justify-between gap-4 hover:shadow-md hover:border-slate-200 dark:hover:border-slate-700 transition-all">
                    <div>
                      <p className="text-base font-black text-slate-900 dark:text-white">{tpl.label}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-1">{tpl.description}</p>
                    </div>
                    <button
                      onClick={() => handleApplyTemplate(tpl.id)}
                      disabled={applyingTemplate === tpl.id}
                      className="shrink-0 flex items-center gap-1.5 px-4 py-2 bg-brand text-white text-2xs font-black uppercase tracking-wider rounded-xl hover:bg-brand-hover transition-all disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed"
                    >
                      {applyingTemplate === tpl.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                      {t('common.apply', 'Apply')}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {viewTab === 'tasks' && (
        <div role="tabpanel" id="tasks-panel" className="animate-in fade-in duration-300">
      {/* ✅ PENDING APPROVAL PANEL — visible to OWNER/ADMIN only */}
      {isAdmin && currentHouseholdId && (() => {
        const pendingTasks = state.tasks.filter(t => t.status === 'pending_approval' && !t.isDeleted);
        if (pendingTasks.length === 0) return null;
        return (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700 rounded-2xl p-5">
            <h2 className="text-[11px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {t('tasks.pending_approval', 'Pending Approval')} ({pendingTasks.length})
            </h2>
            <div className="space-y-2">
              {pendingTasks.map(task => (
                <div key={task.id} className="flex items-center justify-between gap-3 bg-white dark:bg-slate-800 rounded-xl px-4 py-3 border border-amber-100 dark:border-amber-800">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{task.title}</p>
                    {task.assigneeId && (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {householdMembers.find(m => m.userId === task.assigneeId)?.name || task.assigneeId}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleApproveTask(task)}
                      disabled={approvingTaskId === task.id}
                      className="flex items-center gap-1 px-4 py-2 bg-brand text-white text-2xs font-black uppercase tracking-wider rounded-xl hover:bg-brand-hover transition-colors disabled:bg-slate-300 disabled:text-slate-500"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      {t('tasks.approve', 'Approve')}
                    </button>
                    <button
                      onClick={() => handleRejectTask(task)}
                      disabled={approvingTaskId === task.id}
                      className="flex items-center gap-1 px-4 py-2 bg-red-100 text-red-700 text-2xs font-black uppercase tracking-wider rounded-xl hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      {t('tasks.reject', 'Reject')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {tasks.length === 0 && (
        <EmptyState
          icon={<PlusCircle className="w-8 h-8 text-slate-400" />}
          title={t('tasks.empty_title', 'No tasks yet')}
          description={t('tasks.empty_desc', 'Create your first task to get started.')}
          className="py-20"
        >
          <button
            onClick={handleNewTaskClick}
            disabled={isCheckingLimit}
            className="mt-2 px-6 py-2.5 bg-brand text-white text-sm font-semibold rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-50"
          >
            {t('tasks.createTask', 'Create Task')}
          </button>
        </EmptyState>
      )}

      <DndContext
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <KanbanBoard
          config={DEFAULT_TASKS_BOARD}
          tasks={tasks}
          onAddTask={handleNewTaskClick}
          onViewTask={(task: KanbanTask) => { setShowTaskModal(true); setSelectedTask(task); }}
          onMoveTask={handleKanbanBoardMove}
          currentUserId={currentUserId}
          currentHouseholdId={currentHouseholdId}
          activeDataScope={activeScope}
          onDataScopeChange={(scope) => dispatch({ type: 'SET_DATA_SCOPE', payload: scope })}
        />
      </DndContext>
        </div>
      )}

      {/* 🤖 AI CHORE PLANNER PANEL */}
      {showChorePanel && (
        <div className="fixed inset-0 z-[80] flex justify-end">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowChorePanel(false)} />
          <aside 
            role="dialog"
            aria-modal="true"
            aria-labelledby="drawer-title"
            className="relative w-full md:w-96 bg-white dark:bg-slate-900 border-l border-slate-100 dark:border-slate-800 shadow-2xl flex flex-col h-full animate-in slide-in-from-right-8 duration-300 p-6"
          >
            {/* Header */}
            <div className="p-0 border-0 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-brand" />
                </div>
                <h2 id="drawer-title" className="text-sm font-black uppercase tracking-widest text-slate-900 dark:text-white">{t('tasks.ai_chore_planner', 'AI Chore Planner')}</h2>
              </div>
              <button onClick={() => setShowChorePanel(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {/* Controls */}
            <div className="p-0 border-0 mb-6 space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-black text-slate-500 uppercase tracking-wider">Plan for</span>
                <input
                  type="number"
                  min={3} max={14}
                  value={chorePlanDays}
                  onChange={e => setChorePlanDays(Math.min(14, Math.max(3, Number(e.target.value))))}
                  className="w-16 px-2 py-1 text-sm font-bold text-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
                />
                <span className="text-[11px] font-black text-slate-500 uppercase tracking-wider">days</span>
              </div>
              <button
                onClick={handleGenerateChoreSchedule}
                disabled={choreGenLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-brand text-white rounded-full font-black uppercase tracking-widest text-xs shadow-lg hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand dark:focus-visible:ring-offset-slate-900 active:bg-blue-800 disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none transition-all"
              >
                {choreGenLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {choreGenLoading ? t('tasks.generating', 'Generating...') : t('tasks.generate_plan', 'Generate Plan')}
              </button>
              {choreGenError && (
                <p className="text-2xs text-red-500 font-bold">{choreGenError}</p>
              )}
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto space-y-3 no-scrollbar">
              {generatedChores.length === 0 && !choreGenLoading && (
                <div className="flex flex-col items-center justify-center h-32 text-slate-400 space-y-2">
                  <Sparkles className="w-8 h-8 opacity-30" />
                  <p className="text-2xs font-bold uppercase tracking-wider">Generate a plan to see chores</p>
                </div>
              )}
              {generatedChores.map((chore, i) => (
                <div key={i} className={`p-4 rounded-2xl border-2 transition-all ${addedChoreIds.has(i) ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 opacity-60' : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-black text-slate-900 dark:text-white leading-tight">{chore.title}</p>
                      <p className="text-2xs text-slate-500 dark:text-slate-300 mt-1">{chore.assigneeName} · {chore.dueDate}</p>
                      <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                        <span className="text-[9px] font-black text-blue-800 dark:text-blue-200 uppercase">{chore.contributionPoints} pts</span>
                      </div>
                    </div>
                    {!addedChoreIds.has(i) ? (
                      <button onClick={() => handleAddGeneratedChore(chore, i)} className="shrink-0 w-8 h-8 bg-brand text-white rounded-full flex items-center justify-center hover:bg-brand-hover transition-colors">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <div className="shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}

      {/* Plan limit gate */}
      <UpgradeModal
        open={upgradeLimitResult !== null}
        onClose={() => setUpgradeLimitResult(null)}
        result={upgradeLimitResult}
        householdId={currentHouseholdId ?? null}
      />

      {/* TODO: Implement TaskModal Component */}
      {showTaskModal && (
        <TaskModal
          task={selectedTask}
          onClose={() => setShowTaskModal(false)}
          onCreate={createTask}
          onUpdate={updateTask}
          onDelete={deleteTask}
          currentUserId={currentUserId}
          currentHouseholdId={currentHouseholdId}
          availableBoards={[DEFAULT_TASKS_BOARD]}
          householdMembers={householdMembers}
          initialTask={initialTaskData}
        />
      )}
    </div>
    </>
  );
};